// Death-recap coach (roadmap #54): one line on the death screen that says WHY
// you died and what to do about it. Lines rotate per cause and quiet down
// once seen enough — except the gunfire matchup lines, which carry fresh
// information per killer plane and never go quiet. Client-side table by
// design: copy iterates too fast to justify a server round-trip.

const QUIET_AFTER = 6; // after this many, show a cause's line every 3rd death

const LINES = {
  stall: [
    "You stalled — the ⚠ STALL buffet was the warning. Ease the pull, nose down, build speed.",
    "Low and slow is how planes die. Trade altitude for speed before you turn hard.",
  ],
  terrain: [
    "Terrain: 1, you: 0. In a dive, start the pull-out at twice the altitude you think you need.",
    "The island doesn't move. Keep one eye on the ALT readout in a fight down low.",
  ],
  water: [
    "The sea is just as hard as the ground. Watch your altitude over the waves — it's hard to judge.",
    "Splashed. Low passes over water need a hand on the pitch and an eye on ALT.",
  ],
  flak: [
    "Flak got you — it can't lead a turning target. Jink near guns, and never fly straight over a base.",
    "AA guns solve for straight lines. Change heading and altitude every few seconds near a base.",
  ],
  blast: [
    "Caught in a blast — give bombers and their targets a wider berth.",
  ],
  bounds: [
    "The map edge bites. Turn back when the warning shows.",
  ],
  cable: [
    "Balloon cables shred planes. Thread the gaps, climb over the fence — or shoot the balloons down first.",
  ],
  gunfireEven: [
    "{killer} beat you to the shot. Check six often — the ⚠ CHECK SIX warning buys you one break turn.",
    "Shot down by {killer}. Don't fly straight for more than a few seconds in a fight.",
  ],
  gunfireTheyTurn: [
    "{killer} out-turned you — the {killerPlane} wins by circling. Use speed: boom-and-zoom, don't turn with it.",
  ],
  gunfireYouTurn: [
    "{killer} had energy on you. Drag the fight into a turning circle — the {yourPlane} wins it there.",
  ],
};

function seenCounts() {
  try {
    return JSON.parse(localStorage.getItem("va-coach-seen") || "{}") || {};
  } catch {
    return {};
  }
}

/**
 * Pick the coach line for a death. Returns { line, cause, index } or null
 * when the cause is unknown or currently quieted. Bumps the seen count.
 *
 * cause            server cause string from the kill event's Text
 * killerName       display name of the killer ("" for environment deaths)
 * killerPlaneIdx   killer's aircraft type index (-1 = not a flying player)
 * yourPlaneIdx     the victim's aircraft type index
 * planeTypes       config.aircraftTypes (names + cmElevator agility stat)
 */
export function coachLineFor(cause, killerName, killerPlaneIdx, yourPlaneIdx, planeTypes) {
  if (!cause) return null;
  let key = cause;
  let matchup = false;
  if (cause === "gunfire") {
    // Matchup advice from relative agility — the same cmElevator stat the
    // hangar's AGIL bar shows. Only when the killer flew a plane.
    const ka = planeTypes?.[killerPlaneIdx]?.cmElevator;
    const ya = planeTypes?.[yourPlaneIdx]?.cmElevator;
    if (ka != null && ya != null && killerPlaneIdx !== yourPlaneIdx) {
      key = ka > ya ? "gunfireTheyTurn" : ka < ya ? "gunfireYouTurn" : "gunfireEven";
      matchup = key !== "gunfireEven";
    } else {
      key = "gunfireEven";
    }
  }
  const lines = LINES[key];
  if (!lines) return null;

  const seen = seenCounts();
  const count = seen[key] ?? 0;
  seen[key] = count + 1;
  try {
    localStorage.setItem("va-coach-seen", JSON.stringify(seen));
  } catch {
    /* private mode: coach just never quiets */
  }
  // Quiet down well-worn advice; matchup lines stay (fresh info per killer).
  if (!matchup && count >= QUIET_AFTER && count % 3 !== 0) return null;

  const index = count % lines.length;
  const line = lines[index]
    .replaceAll("{killer}", killerName || "The enemy")
    .replaceAll("{killerPlane}", planeTypes?.[killerPlaneIdx]?.name ?? "enemy plane")
    .replaceAll("{yourPlane}", planeTypes?.[yourPlaneIdx]?.name ?? "your plane");
  return { line, cause, index };
}
